import React, { useEffect, useRef, useState } from 'react';
import { connect } from 'react-redux';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import DataTable from 'react-data-table-component';

import { API_BASE_URL } from '../../config.js';
import Pagination from '../Pagination/Paginations';
import EntriesSelector from '../Pagination/EntriesSelector';

const HrGatepass = ({ fixNavbar }) => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [toggleCleared, setToggleCleared] = useState(false);
  const [approving, setApproving] = useState(false);
  const approvingRef = useRef(false);

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem('authToken')}` },
  };

  /* ================= FETCH DATA ================= */
  const loadData = () => {
    axios
      .get(API_BASE_URL + '/hr_gatepass_list', authHeader)
      .then((res) => setData(res.data || []))
      .catch(() => toast.error('Failed to load gate pass list'));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ================= SEARCH ================= */
  const filteredData = data.filter((item) =>
    `${item.employee_name}${item.emp_code}${item.department_name}${item.from_date}${item.to_date}${item.reason}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  /* ================= PAGINATION (Previous / Next style) ================= */
  const totalPages = Math.ceil(filteredData.length / perPage);
  const indexOfLast = currentPage * perPage;
  const indexOfFirst = indexOfLast - perPage;
  const pageData = filteredData.slice(indexOfFirst, indexOfLast);

  /* ================= STATUS LABEL ================= */
  const statusBadge = (status) => {
    const s = Number(status);
    if (s === 2) return <span className="badge badge-success">Approved</span>;
    return <span className="badge badge-warning">Pending</span>;
  };

  /* ================= ROW SELECTION ================= */
  const handleSelectedRowsChange = ({ selectedRows }) => {
    setSelectedRows(selectedRows);
  };

  const handleApprove = () => {
    // guard against double / rapid clicks while an approve is already in flight
    if (approvingRef.current) return;

    if (selectedRows.length === 0) {
      toast.error('Please select at least one row', { toastId: 'gatepass-approve-validation' });
      return;
    }

    const ids = selectedRows.map((r) => r.id);

    approvingRef.current = true;
    setApproving(true);
    axios
      .post(API_BASE_URL + '/gatepass_approve', { ids }, authHeader)
      .then((res) => {
        if (res.data && res.data.status === 200) {
          toast.success(res.data.message || 'Approved successfully');
          setSelectedRows([]);
          setToggleCleared(!toggleCleared);
          loadData();
        } else {
          toast.error((res.data && res.data.message) || 'Failed to approve', { toastId: 'gatepass-approve' });
        }
      })
      .catch(() => toast.error('Failed to approve gate pass', { toastId: 'gatepass-approve' }))
      .finally(() => {
        approvingRef.current = false;
        setApproving(false);
      });
  };

  /* ================= PROFILE IMAGE ================= */
  const profileBase = API_BASE_URL.replace('/api', '') + '/uploads/profile/';

  /* ================= DATATABLE COLUMNS ================= */
  const columns = [
    {
      name: 'S.No',
      selector: (row, index) => index + 1,
      width: '80px',
    },
    {
      name: 'Image',
      width: '90px',
      cell: (row) =>
        row.profile_picture ? (
          <div className="gp-img-zoom">
            <img
              src={`${profileBase}${row.profile_picture}`}
              alt={row.employee_name}
              style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                objectFit: 'cover',
              }}
            />
            <img
              className="gp-img-large"
              src={`${profileBase}${row.profile_picture}`}
              alt={row.employee_name}
            />
          </div>
        ) : (
          <span
            className="avatar avatar-blue"
            style={{
              width: '40px',
              height: '40px',
              borderRadius: '50%',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {(row.employee_name || '?').charAt(0).toUpperCase()}
          </span>
        ),
    },
    {
      name: 'Employee Name',
      selector: (row) => row.employee_name,
      sortable: true,
      grow: 2,
      cell: (row) => (
        <div>
          <div>{row.employee_name}</div>
          <small className="text-muted">{row.emp_code}</small>
        </div>
      ),
    },
    {
      name: 'Department',
      selector: (row) => row.department_name || '-',
      sortable: true,
      wrap: true,
      grow: 2,
      minWidth: '160px',
    },
    {
      name: 'From Date',
      selector: (row) => row.from_date,
      sortable: true,
      minWidth: '130px',
    },
    {
      name: 'To Date',
      selector: (row) => row.to_date,
      sortable: true,
      minWidth: '130px',
    },
    {
      name: 'Type',
      selector: (row) => row.type || '-',
      sortable: true,
      width: '90px',
      cell: (row) => (
        <span
          className={
            'badge ' +
            (row.type === 'OUT' ? 'badge-danger' : 'badge-info')
          }
        >
          {row.type || '-'}
        </span>
      ),
    },
    {
      name: 'Time',
      selector: (row) => row.pass_time || '-',
      sortable: true,
      width: '100px',
    },
    {
      name: 'Reason',
      selector: (row) => row.reason || '',
      sortable: true,
      wrap: true,
      grow: 2,
      cell: (row) => {
        const reason = row.reason || '';
        const shortText =
          reason.length > 5 ? reason.substring(0, 5) + '...' : reason;
        return (
          <div className="remark-hover-wrapper">
            {shortText}
            <div className="remark-hover-box gp-scroll gp-left">{reason}</div>
          </div>
        );
      },
    },
    {
      name: 'Status',
      selector: (row) => row.status,
      sortable: true,
      cell: (row) => statusBadge(row.status),
    },
  ];

  return (
    <>
      <ToastContainer position="bottom-right" />

      <div className={`section-body ${fixNavbar ? 'marginTop' : ''}`}>
        <div className="container-fluid">

          <div className="card">
            {/* HEADER */}
            <div className="card-header d-flex justify-content-between align-items-center">
          <h3 className="card-title d-flex align-items-center mb-0" style={{ color: "#1d3a8a", fontWeight: 600 }}>
            <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 16 16" fill="#1d3a8a" style={{ marginRight: "8px", verticalAlign: "-3px" }}>
              <path d="M5 3a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2V5a2 2 0 0 0-2-2zm.5 2a.5.5 0 1 1 0-1 .5.5 0 0 1 0 1M8 4a.5.5 0 1 1 0 1 .5.5 0 0 1 0-1M2 2a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1z"/>
            </svg>
            Manage Gatepass
          </h3>

              <div className="d-flex align-items-center" style={{ gap: '10px' }}>
              <EntriesSelector
                entriesPerPage={perPage}
                setEntriesPerPage={setPerPage}
                setCurrentPage={setCurrentPage}
              />
              <div style={{ position: 'relative', width: '260px' }}>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="Search"
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  style={{ paddingRight: '30px' }}
                />
                {searchTerm && (
                  <span
                    onClick={() => { setSearchTerm(''); setCurrentPage(1); }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      right: '10px',
                      transform: 'translateY(-50%)',
                      cursor: 'pointer',
                      color: '#aaa',
                      fontSize: '18px',
                    }}
                  >
                    ✖
                  </span>
                )}
              </div>
              </div>
            </div>

            {/* TABLE */}
            <div className="card-body">
              <DataTable
                className="hide-disabled-checkbox"
                columns={columns}
                data={pageData}
                selectableRows
                selectableRowDisabled={(row) => Number(row.status) === 2}
                onSelectedRowsChange={handleSelectedRowsChange}
                clearSelectedRows={toggleCleared}
                highlightOnHover
                striped
                responsive
                persistTableHead
                noDataComponent="No gate pass records found"
              />

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={setCurrentPage}
              />

              {/* APPROVE BUTTON */}
              <div className="mt-3">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleApprove}
                  disabled={approving || selectedRows.length === 0}
                >
                  {approving ? 'Approving...' : 'Approve Gatepass'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  fixNavbar: state.settings.isFixNavbar,
});

const mapDispatchToProps = () => ({});

export default connect(mapStateToProps, mapDispatchToProps)(HrGatepass);
